<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    syaratKetentuan: Object
});

const activeTab = ref('basic');
const saving = ref(false);
const modalType = ref('error');
const errorModalContent = ref([]);

// Editors
let contentEditor = null;

// Forms
const basicForm = ref({
    title: '',
    hero_title: '',
    hero_subtitle: '',
    intro_text: ''
});

const contentForm = ref({
    content: ''
});

const seoForm = ref({
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
});

// Original data for change detection
let originalBasic = {};
let originalContent = '';
let originalSeo = {};

// Show modal helper
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    try {
        const modalEl = document.getElementById('notificationModal');
        if (modalEl && typeof bootstrap !== 'undefined') {
            const modal = new bootstrap.Modal(modalEl);
            modal.show();
        }
    } catch (e) {
        alert(errorModalContent.value.join('\n'));
    }
};

// Normalize HTML for comparison
const normalizeHtml = (html) => {
    if (!html) return '';
    return html.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
};

const initData = () => {
    if (props.syaratKetentuan) {
        basicForm.value = {
            title: props.syaratKetentuan.title || '',
            hero_title: props.syaratKetentuan.hero_title || '',
            hero_subtitle: props.syaratKetentuan.hero_subtitle || '',
            intro_text: props.syaratKetentuan.intro_text || ''
        };
        contentForm.value = {
            content: props.syaratKetentuan.content || ''
        };
        seoForm.value = {
            meta_title: props.syaratKetentuan.meta_title || '',
            meta_description: props.syaratKetentuan.meta_description || '',
            meta_keywords: props.syaratKetentuan.meta_keywords || ''
        };
    }
};

const storeOriginalData = () => {
    originalBasic = { ...basicForm.value };
    originalContent = normalizeHtml(contentForm.value.content);
    originalSeo = { ...seoForm.value };
};

const updateBasic = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Validation
    const errors = [];
    if (!basicForm.value.title?.trim()) errors.push('Judul Halaman wajib diisi');
    
    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }
    
    // Check for changes
    if (JSON.stringify(basicForm.value) === JSON.stringify(originalBasic)) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put('/admin/syarat-ketentuan/update-basic', basicForm.value);
        if (response.data.status === 'success') {
            originalBasic = { ...basicForm.value };
        }
    } catch (e) {
        showModal('error', 'Gagal menyimpan data dasar');
    } finally {
        saving.value = false;
    }
};

const updateContent = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Sync CKEditor
    if (contentEditor) {
        contentForm.value.content = contentEditor.getData();
    }
    
    // Validation
    if (!contentForm.value.content?.trim()) {
        saving.value = false;
        showModal('error', 'Konten wajib diisi');
        return;
    }
    
    // Check for changes
    if (normalizeHtml(contentForm.value.content) === originalContent) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put('/admin/syarat-ketentuan/update-content', {
            content: contentForm.value.content
        });
        if (response.data.status === 'success') {
            originalContent = normalizeHtml(contentForm.value.content);
        }
    } catch (e) {
        showModal('error', 'Gagal menyimpan konten');
    } finally {
        saving.value = false;
    }
};

const updateSeo = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Check for changes
    if (JSON.stringify(seoForm.value) === JSON.stringify(originalSeo)) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    try {
        const response = await axios.put('/admin/syarat-ketentuan/update-seo', seoForm.value);
        if (response.data.status === 'success') {
            originalSeo = { ...seoForm.value };
        }
    } catch (e) {
        showModal('error', 'Gagal menyimpan SEO');
    } finally {
        saving.value = false;
    }
};

// Init CKEditor
const initCKEditor = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }
    
    if (CKEDITOR.instances['sk_content']) {
        CKEDITOR.instances['sk_content'].destroy();
    }
    
    contentEditor = CKEDITOR.replace('sk_content', {
        height: 400,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'styles', items: ['Format'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        removePlugins: 'elementspath',
        resize_enabled: true
    });
    
    contentEditor.setData(contentForm.value.content || '');
};

onMounted(() => {
    initData();
    storeOriginalData();
    nextTick(() => {
        initCKEditor();
    });
});

onBeforeUnmount(() => {
    if (contentEditor) {
        try {
            contentEditor.destroy();
        } catch (e) {
            console.warn('CKEditor destroy error:', e);
        }
        contentEditor = null;
    }
});
</script>

<template>
    <Head title="Syarat dan Ketentuan" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Syarat dan Ketentuan</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">Syarat dan Ketentuan</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header p-0 border-bottom-0">
                            <ul class="nav nav-tabs" role="tablist">
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'basic' }" @click.prevent="activeTab = 'basic'" href="#">Info Dasar</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'content' }" @click.prevent="activeTab = 'content'" href="#">Konten</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'seo' }" @click.prevent="activeTab = 'seo'" href="#">SEO</a>
                                </li>
                            </ul>
                        </div>
                        <div class="card-body">
                            <div class="tab-content">
                                <!-- Basic Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'basic' }">
                                    <form @submit.prevent="updateBasic">
                                        <div class="form-group mb-3">
                                            <label for="sk_title" class="form-label">Judul Halaman <span class="text-danger">*</span></label>
                                            <input id="sk_title" name="sk_title" type="text" v-model="basicForm.title" class="form-control">
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="sk_hero_title" class="form-label">Hero Title</label>
                                            <input id="sk_hero_title" name="sk_hero_title" type="text" v-model="basicForm.hero_title" class="form-control">
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="sk_hero_subtitle" class="form-label">Hero Subtitle</label>
                                            <input id="sk_hero_subtitle" name="sk_hero_subtitle" type="text" v-model="basicForm.hero_subtitle" class="form-control">
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="sk_intro" class="form-label">Intro Text</label>
                                            <textarea id="sk_intro" name="sk_intro" v-model="basicForm.intro_text" class="form-control" rows="3"></textarea>
                                        </div>
                                        <div class="text-end">
                                            <button type="submit" class="btn btn-primary" :disabled="saving">
                                                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <!-- Content Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'content' }">
                                    <form @submit.prevent="updateContent">
                                        <div class="form-group mb-3">
                                            <label for="sk_content" class="form-label">Konten Utama <span class="text-danger">*</span></label>
                                            <textarea id="sk_content" name="sk_content" class="form-control" rows="20"></textarea>
                                        </div>
                                        <div class="text-end">
                                            <button type="submit" class="btn btn-primary" :disabled="saving">
                                                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <!-- SEO Tab -->
                                <div class="tab-pane fade" :class="{ 'show active': activeTab === 'seo' }">
                                    <form @submit.prevent="updateSeo">
                                        <div class="form-group mb-3">
                                            <label for="sk_meta_title" class="form-label">Meta Title</label>
                                            <input id="sk_meta_title" name="sk_meta_title" type="text" v-model="seoForm.meta_title" class="form-control">
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="sk_meta_description" class="form-label">Meta Description</label>
                                            <textarea id="sk_meta_description" name="sk_meta_description" v-model="seoForm.meta_description" class="form-control" rows="3"></textarea>
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="sk_meta_keywords" class="form-label">Meta Keywords</label>
                                            <input id="sk_meta_keywords" name="sk_meta_keywords" type="text" v-model="seoForm.meta_keywords" class="form-control">
                                        </div>
                                        <div class="text-end">
                                            <button type="submit" class="btn btn-primary" :disabled="saving">
                                                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Notification Modal -->
        <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" 
                        :class="{
                            'bg-danger': modalType === 'error',
                            'bg-warning text-dark': modalType === 'warning',
                            'bg-success': modalType === 'success',
                            'bg-info': modalType === 'info'
                        }">
                        <h5 class="modal-title">
                            <i class="me-2" :class="{
                                'ti-alert': modalType === 'error',
                                'fa fa-exclamation-triangle': modalType === 'warning',
                                'fa fa-check-circle': modalType === 'success',
                                'fa fa-info-circle': modalType === 'info'
                            }"></i>
                            {{ modalType === 'error' ? 'Peringatan Validasi' : modalType === 'warning' ? 'Peringatan' : modalType === 'success' ? 'Berhasil' : 'Informasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" 
                                :class="{
                                    'list-group-item-danger': modalType === 'error',
                                    'list-group-item-warning': modalType === 'warning',
                                    'list-group-item-success': modalType === 'success',
                                    'list-group-item-info': modalType === 'info'
                                }" 
                                v-for="(msg, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="{
                                    'fa-exclamation-circle': modalType === 'error',
                                    'fa-exclamation-triangle': modalType === 'warning',
                                    'fa-check-circle': modalType === 'success',
                                    'fa-info-circle': modalType === 'info'
                                }"></i>{{ msg }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" 
                            :class="{
                                'btn-danger': modalType === 'error',
                                'btn-warning': modalType === 'warning',
                                'btn-success': modalType === 'success',
                                'btn-info': modalType === 'info'
                            }" 
                            data-bs-dismiss="modal">
                            <i class="fa fa-check-square-o me-2"></i>Mengerti
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
